import authApiService from "./userApiService"; // Ensure this is your axios instance with interceptors

// Helper function to handle errors
const handleError = (error) => {
  console.error('API Error:', error);
  return {
    success: false,
    error: error.response?.data?.message || error.response?.data?.error || error.message || 'An unexpected error occurred',
    statusCode: error.response?.status,
    details: error.response?.data?.details || null
  };
};

// Helper function to handle successful responses
const handleSuccess = (response, message = null) => {
  return {
    success: true,
    data: response.data,
    message: message || response.data?.message || 'Operation completed successfully',
    statusCode: response.status
  };
};

export const promosTransactionService = {
  // ============================================================================
  // TRANSACTION CRUD OPERATIONS (RESTful)
  // ============================================================================

  // Get all transactions
  async getAllTransactions() {
    try {
      const response = await authApiService.get("/promos/transactions");
      return handleSuccess(response, 'Transactions retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get transaction by ID
  async getTransactionById(id) {
    try {
      const response = await authApiService.get(`/promos/transactions/${id}`);
      return handleSuccess(response, 'Transaction retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Add new transaction
  async addTransaction(transactionData) {
    try {
      const payload = {
        transactionType: transactionData.transactionType,
        amount: parseFloat(transactionData.amount),
        notes: transactionData.notes || '',
        playerName: transactionData.playerName || '',
        playerNumber: transactionData.playerNumber || '',
      };

      const response = await authApiService.post("/promos/transactions", payload);
      return handleSuccess(response, 'Transaction added successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Update transaction by ID
  async updateTransaction(id, transactionData) {
    try {
      const payload = {
        date: transactionData.date,
        playerName: transactionData.playerName,
        notes: transactionData.notes
      };

      const response = await authApiService.put(`/promos/transactions/${id}`, payload);
      return handleSuccess(response, 'Transaction updated successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Delete transaction by ID
  async deleteTransaction(id) {
    try {
      const response = await authApiService.delete(`/promos/transactions/${id}`);
      return handleSuccess(response, 'Transaction deleted successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // ============================================================================
  // BALANCE OPERATIONS
  // ============================================================================

  // Get current balance
  async getCurrentBalance() {
    try {
      const response = await authApiService.get("/promos/balance");
      return handleSuccess(response, 'Balance retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // ============================================================================
  // VERIFICATION OPERATIONS
  // ============================================================================

  // Verify balance (Admin/Management only)
  async verifyBalance(verificationData) {
    try {
      const payload = {
        promosCashAmount: parseFloat(verificationData.promosCashAmount)
      };

      const response = await authApiService.post("/promos/verify", payload);
      return handleSuccess(response, 'Balance verification completed');
    } catch (error) {
      return handleError(error);
    }
  },

  // Check verification status
  async checkVerificationStatus() {
    try {
      const response = await authApiService.get("/promos/verification-status");
      return handleSuccess(response, 'Verification status retrieved');
    } catch (error) {
      return handleError(error);
    }
  },

  // Verify specific transaction (Admin/Management only)
  async verifySpecificTransaction(id, verificationData) {
    try {
      const payload = {
        promosCashAmount: parseFloat(verificationData.promosCashAmount)
      };

      const response = await authApiService.post(`/promos/transactions/${id}/verify`, payload);
      return handleSuccess(response, 'Transaction verification completed');
    } catch (error) {
      return handleError(error);
    }
  },

  // ============================================================================
  // LOGGING & MONITORING
  // ============================================================================

  // Log wrong time entry attempts
  async logWrongTimeEntry(data) {
    try {
      const payload = {
        username: data.username,
        attemptTime: data.attemptTime || new Date().toISOString(),
        transactionType: data.transactionType || '',
        amount: data.amount || 0,
        notes: data.notes || 'Wrong time entry attempt'
      };

      const response = await authApiService.post("/promos/log-wrong-time", payload);
      return handleSuccess(response, 'Wrong time entry logged successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Health check
  async healthCheck() {
    try {
      const response = await authApiService.get("/promos/health");
      return handleSuccess(response, 'Service is healthy');
    } catch (error) {
      return handleError(error);
    }
  },

  // ============================================================================
  // PROMO CATALOG (admin-managed dropdown options)
  // ============================================================================

  async getPromoOptions() {
    try {
      const response = await authApiService.get("/promos/promo-options");
      return handleSuccess(response, 'Promo options retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  async savePromoOptions(options) {
    try {
      const response = await authApiService.put("/promos/promo-options", { options });
      return handleSuccess(response, 'Promo options saved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // ============================================================================
  // LEGACY/BACKWARD COMPATIBILITY METHODS
  // ============================================================================

  // Alternative method using traditional route
  async getTransactions() {
    try {
      const response = await authApiService.get("/promos/get-transactions");
      return handleSuccess(response, 'Transactions retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Alternative method using traditional route
  async getBalance() {
    try {
      const response = await authApiService.get("/promos/get-balance");
      return handleSuccess(response, 'Balance retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Alternative method using traditional route
  async createTransaction(transactionData) {
    try {
      const payload = {
        transactionType: transactionData.transactionType,
        amount: parseFloat(transactionData.amount),
        notes: transactionData.notes || '',
        playerName: transactionData.playerName || ''
      };

      const response = await authApiService.post("/promos/add-transaction", payload);
      return handleSuccess(response, 'Transaction created successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Alternative method using traditional route
  async editTransaction(transactionData) {
    try {
      const payload = {
        id: transactionData.id,
        date: transactionData.date,
        playerName: transactionData.playerName,
        notes: transactionData.notes
      };

      const response = await authApiService.put(`/promos/edit-transaction/${transactionData.id}`, payload);
      return handleSuccess(response, 'Transaction edited successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Alternative method using traditional route
  async removeTransaction(id) {
    try {
      const response = await authApiService.delete(`/promos/delete-transaction/${id}`);
      return handleSuccess(response, 'Transaction removed successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Alternative method using traditional route
  async verifyPromoBalance(verificationData) {
    try {
      const payload = {
        promosCashAmount: parseFloat(verificationData.promosCashAmount)
      };

      const response = await authApiService.post("/promos/verify-balance", payload);
      return handleSuccess(response, 'Promo balance verified successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Alternative method using traditional route
  async checkVerification() {
    try {
      const response = await authApiService.get("/promos/check-verification");
      return handleSuccess(response, 'Verification status checked');
    } catch (error) {
      return handleError(error);
    }
  },

  // ============================================================================
  // ADVANCED OPERATIONS
  // ============================================================================

  // Batch operations for multiple transactions
  async batchDeleteTransactions(transactionIds) {
    try {
      const deletePromises = transactionIds.map(id => this.deleteTransaction(id));
      const results = await Promise.allSettled(deletePromises);
      
      const successful = results.filter(result => result.status === 'fulfilled' && result.value.success);
      const failed = results.filter(result => result.status === 'rejected' || !result.value.success);

      return {
        success: true,
        data: {
          totalProcessed: transactionIds.length,
          successful: successful.length,
          failed: failed.length,
          successfulIds: successful.map((_, index) => transactionIds[index]),
          failedIds: failed.map((_, index) => transactionIds[index]),
          results: results
        },
        message: `Batch delete completed: ${successful.length} successful, ${failed.length} failed`
      };
    } catch (error) {
      return handleError(error);
    }
  },

  // Batch operations for multiple transaction updates
  async batchUpdateTransactions(transactionUpdates) {
    try {
      const updatePromises = transactionUpdates.map(update => 
        this.updateTransaction(update.id, update.data)
      );
      const results = await Promise.allSettled(updatePromises);
      
      const successful = results.filter(result => result.status === 'fulfilled' && result.value.success);
      const failed = results.filter(result => result.status === 'rejected' || !result.value.success);

      return {
        success: true,
        data: {
          totalProcessed: transactionUpdates.length,
          successful: successful.length,
          failed: failed.length,
          results: results
        },
        message: `Batch update completed: ${successful.length} successful, ${failed.length} failed`
      };
    } catch (error) {
      return handleError(error);
    }
  },

  // Get transactions with filters and pagination
  async getFilteredTransactions(filters = {}, pagination = {}) {
    try {
      const params = {};
      
      // Filter parameters
      if (filters.transactionType) params.transactionType = filters.transactionType;
      if (filters.playerName) params.playerName = filters.playerName;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.addedBy) params.addedBy = filters.addedBy;
      if (filters.minAmount) params.minAmount = filters.minAmount;
      if (filters.maxAmount) params.maxAmount = filters.maxAmount;
      
      // Pagination parameters
      if (pagination.page) params.page = pagination.page;
      if (pagination.limit) params.limit = pagination.limit;
      if (pagination.sortBy) params.sortBy = pagination.sortBy;
      if (pagination.sortOrder) params.sortOrder = pagination.sortOrder;

      const response = await authApiService.get("/promos/transactions", { params });
      return handleSuccess(response, 'Filtered transactions retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get transaction summary/statistics
  async getTransactionSummary(period = 'daily', filters = {}) {
    try {
      const params = { period, ...filters };
      const response = await authApiService.get("/promos/transactions/summary", { params });
      return handleSuccess(response, 'Transaction summary retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get transaction analytics
  async getTransactionAnalytics(dateRange = {}, groupBy = 'day') {
    try {
      const params = {
        groupBy,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      };
      const response = await authApiService.get("/promos/transactions/analytics", { params });
      return handleSuccess(response, 'Transaction analytics retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Export transactions
  async exportTransactions(format = 'csv', filters = {}) {
    try {
      const params = { format, ...filters };
      const response = await authApiService.get("/promos/transactions/export", { 
        params,
        responseType: 'blob' // For file downloads
      });
      return { 
        success: true, 
        data: response.data, 
        headers: response.headers,
        message: 'Transactions exported successfully'
      };
    } catch (error) {
      return handleError(error);
    }
  },

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  // Validate transaction data before sending
  validateTransactionData(transactionData) {
    const errors = [];
    
    if (!transactionData.transactionType) {
      errors.push('Transaction type is required');
    } else if (!['deposit', 'withdraw'].includes(transactionData.transactionType)) {
      errors.push('Transaction type must be either "deposit" or "withdraw"');
    }
    
    if (!transactionData.amount) {
      errors.push('Amount is required');
    } else if (isNaN(parseFloat(transactionData.amount))) {
      errors.push('Amount must be a valid number');
    } else if (parseFloat(transactionData.amount) <= 0) {
      errors.push('Amount must be greater than 0');
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors
    };
  },

  // Format transaction data for API
  formatTransactionData(transactionData) {
    return {
      transactionType: transactionData.transactionType,
      amount: parseFloat(transactionData.amount),
      notes: transactionData.notes?.trim() || '',
      playerName: transactionData.playerName?.trim() || '',
      date: transactionData.date || new Date().toISOString()
    };
  },

  // Calculate transaction totals
  calculateTransactionTotals(transactions) {
    return transactions.reduce((totals, transaction) => {
      const amount = parseFloat(transaction.transaction_amount || 0);
      
      if (transaction.transaction_type === 'deposit') {
        totals.totalDeposits += amount;
        totals.depositCount += 1;
      } else if (transaction.transaction_type === 'withdraw') {
        totals.totalWithdrawals += amount;
        totals.withdrawalCount += 1;
      }
      
      return totals;
    }, {
      totalDeposits: 0,
      totalWithdrawals: 0,
      depositCount: 0,
      withdrawalCount: 0,
      netAmount: 0
    });
  },

  // Search transactions by multiple criteria
  async searchTransactions(searchCriteria) {
    try {
      const params = {
        search: searchCriteria.query,
        searchFields: searchCriteria.fields?.join(',') || 'player_name,notes,added_by',
        ...searchCriteria.filters
      };

      const response = await authApiService.get("/promos/transactions/search", { params });
      return handleSuccess(response, 'Search completed successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get transaction history for a specific player
  async getPlayerTransactionHistory(playerName, filters = {}) {
    try {
      const params = {
        playerName: playerName,
        ...filters
      };

      const response = await authApiService.get("/promos/transactions/player-history", { params });
      return handleSuccess(response, 'Player transaction history retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  }
};

// Default export for convenience
export default promosTransactionService;